import React, { Fragment, useEffect, useState } from 'react';
import { Form } from '@ant-design/compatible';
import Panel from '../../../../../components/outline/Panel';
import BaseForm from '../../../../../components/form/BaseForm';
import SearchAddress from '../../../../../components/SearchAddress';
import { formRequiredRules, searchAddressRule } from '../../../../../lib/formRules';
import TextAreaForm from '../../../../../components/form/TextAreaForm';
import {
  DELIVERY_INFORMATION_INFO_MAX_LENGTH,
  ORIGIN_ADDRESS_LINE2_MAX_LENGTH,
  RETURN_ADDRESS_LINE2_MAX_LENGTH,
} from '../../../../../constants/service/app/productRegisterConstants';
import InputForm from '../../../../../components/form/InputForm';
import './BrandDetailProductRegisterDelivery.scss';
import SwitchForm from '../../../../event/save/components/SwitchForm';
import SelectForm from '../../../../../components/form/SelectForm';
import { useMutation, useQuery } from '@tanstack/react-query';
import Input from '../../../../../components/input/Input';
import Button from '../../../../../components/Button';
import deliveryEnterprisesApi from '../../../../../lib/api/deliveryEnterprises';
import { asyncSimpleModal } from '../../../../../lib/common';
import RadioGroupForm from '../../../../../components/form/RadioGroupForm';

const TAKEBACK_SIZE_TYPE_SELECT_LIST = [
  {
    id: 'extraSmall',
    name: '극소',
  },
  {
    id: 'small',
    name: '소',
  },
  {
    id: 'medium',
    name: '중',
  },
  {
    id: 'large',
    name: '대',
  },
];

const TAKEBACK_PAYMENT_TYPE_SELECT_LIST = [
  {
    id: 'prepaid',
    name: '선불',
  },
  {
    id: 'collect',
    name: '착불',
  },
  {
    id: 'credit',
    name: '신용',
  },
];

const BrandDetailProductRegisterDelivery = (props) => {
  const { isEditable, data, form } = props;
  const { originAddress, returnAddress, takebackInfo } = data;

  const { line2: originLine2, memo: originMemo, ...originValue } = originAddress || {};
  const { line2: returnLine2, memo: returnMemo, ...returnValue } = returnAddress || {};

  const { useAutoTakeback, deliveryEnterpriseId, takebackPaymentType, takebackSizeType } = takebackInfo || {};

  const deliveryEnterprisesRequest = { status: 'active', autoTakebackEnabled: true };

  const { data: deliveryEnterprises } = useQuery({
    queryKey: ['/delivery-enterprises', deliveryEnterprisesRequest],
    queryFn: () => deliveryEnterprisesApi.getDeliveryEnterprises(deliveryEnterprisesRequest),
    staleTime: Infinity,
    cacheTime: Infinity,
    select: (data) => data.deliveryEnterprises,
  });

  const CJ대한통운_ID = 2;

  const formDeliveryEnterpriseId = form.getFieldValue('deliveryEnterpriseId');

  const formComDivCde = form.getFieldValue('comDivCde');
  const formCusCde = form.getFieldValue('cusCde');

  const showComDivCde = [CJ대한통운_ID].includes(formDeliveryEnterpriseId);

  const { mutateAsync: contractCodeValidations } = useMutation({
    mutationFn: (payload) => deliveryEnterprisesApi.contractCodeValidations(formDeliveryEnterpriseId, payload),
  });

  const handleContractCodeValidations = async (deliveryEnterpriseId, payload) => {
    const { isValid } = await contractCodeValidations({ deliveryEnterpriseId, ...payload });

    return isValid;
  };

  const [cusCdeSuccess, setCusCdeSuccess] = useState(false);
  const [comDivCdeSuccess, setComDivCdeSuccess] = useState(false);

  const [init, setInit] = useState(false);

  useEffect(() => {
    if (init) return;

    if (takebackInfo) {
      setInit(true);

      form.setFieldsValue({
        useAutoTakeback: takebackInfo.useAutoTakeback,
        deliveryEnterpriseId: takebackInfo.deliveryEnterpriseId,
        cusCde: takebackInfo.cusCde,
        comDivCde: takebackInfo.comDivCde,
        takebackPaymentType: takebackInfo.takebackPaymentType,
        takebackSizeType: takebackInfo.takebackSizeType,
      });
    }
  }, [form, init, takebackInfo]);

  return (
    <Panel className="brand-detail-product-register-delivery">
      <div className="panel-header">
        <div className="title">배송 정보</div>
      </div>

      <div className="panel-body">
        {/*출고지*/}
        <BaseForm
          label="출고지"
          required={true}
          content={
            <Fragment>
              <Form.Item className="address-form">
                {form.getFieldDecorator('originAddress', {
                  initialValue: originValue,
                  rules: [searchAddressRule()],
                })(
                  <SearchAddress
                    address={form.getFieldValue('originAddress')}
                    isEditable={isEditable}
                    onSelect={(result) => {
                      form.setFieldsValue({ originAddress: result });
                    }}
                  />,
                )}
              </Form.Item>
              <InputForm
                className="address-line2"
                form={form}
                placeholder="상세주소를 입력해주세요"
                name="originAddressLine2"
                maxLength={ORIGIN_ADDRESS_LINE2_MAX_LENGTH}
                isEditable={isEditable}
                initialValue={originLine2}
                rules={[formRequiredRules(true, '필수 값입니다.')]}
              />
            </Fragment>
          }
        />
        {/*추가 배송정보 (지역별 차등 배송비 정보)*/}
        <TextAreaForm
          label={
            <span>
              추가 배송정보
              <div>(지역별 차등 배송비 정보)</div>
            </span>
          }
          form={form}
          rules={[formRequiredRules(true, '필수 값입니다.')]}
          maxLength={DELIVERY_INFORMATION_INFO_MAX_LENGTH}
          withCount
          name="additionalDeliveryInfo"
          isEditable={isEditable}
          initialValue={originMemo}
        />
        {/*반품/교환지*/}
        <BaseForm
          label="반품/교환지"
          required={true}
          content={
            <Fragment>
              <Form.Item className="address-form">
                {form.getFieldDecorator('returnAddress', {
                  initialValue: returnValue,
                  rules: [searchAddressRule()],
                })(
                  <SearchAddress
                    address={form.getFieldValue('returnAddress')}
                    isEditable={isEditable}
                    onSelect={(result) => {
                      form.setFieldsValue({ returnAddress: result });
                    }}
                  />,
                )}
              </Form.Item>
              <InputForm
                className="address-line2"
                form={form}
                placeholder="상세주소를 입력해주세요"
                name="returnAddressLine2"
                maxLength={RETURN_ADDRESS_LINE2_MAX_LENGTH}
                isEditable={isEditable}
                initialValue={returnLine2}
                rules={[formRequiredRules(true, '필수 값입니다.')]}
              />
            </Fragment>
          }
        />
        {/*반품/교환 정보*/}
        <TextAreaForm
          label="반품/교환 정보"
          form={form}
          rules={[formRequiredRules(true, '필수 값입니다.')]}
          maxLength={DELIVERY_INFORMATION_INFO_MAX_LENGTH}
          withCount
          name="refundExchangeInfo"
          isEditable={isEditable}
          initialValue={returnMemo}
        />
        {/* 반품/교환 자동 회수 접수 */}
        <SwitchForm
          label="반품/교환 자동 회수 접수"
          form={form}
          name="useAutoTakeback"
          isEditable={isEditable}
          disabled={!isEditable}
          initialValue={useAutoTakeback ?? false}
          onChange={() => {
            form.setFieldsValue({
              deliveryEnterpriseId: undefined,
              cusCde: undefined,
              comDivCde: undefined,
              takebackPaymentType: 'collect',
              takebackSizeType: 'extraSmall',
            });
          }}
          subContent={
            isEditable &&
            `자동 회수 설정 시, 유저가 반품/교환 시 회수 방법을 선택할 수 있습니다. (수거 요청/직접 발송 중)
                수거 요청을 선택한 경우 회수 택배가 자동 접수됩니다.`
          }
        />

        {form.getFieldValue('useAutoTakeback') && (
          <>
            {/* 택배사 */}
            <SelectForm
              label="택배사"
              form={form}
              name="deliveryEnterpriseId"
              isEditable={isEditable}
              validateTrigger="onSubmit"
              placeholder="택배사 선택"
              rules={[{ required: true, message: '택배사를 선택해 주세요' }]}
              onChange={async (value) => {
                // 먼저 성공/에러 상태 초기화
                setCusCdeSuccess(false);
                setComDivCdeSuccess(false);

                if (value === null) {
                  await asyncSimpleModal({
                    type: 'error',
                    title: '자동 회수접수 설정 불가',
                    message: `지정 택배사 외에는 자동 회수접수가 불가합니다.
                    자동 회수접수가 비활성됩니다.
                    불편을 드려 죄송합니다.`,
                  });

                  form.setFieldsValue({
                    useAutoTakeback: false,
                    deliveryEnterpriseId: undefined,
                    cusCde: undefined,
                    comDivCde: undefined,
                    takebackPaymentType: undefined,
                    takebackSizeType: undefined,
                  });
                  return;
                }

                form.setFieldsValue({
                  deliveryEnterpriseId: value,
                  cusCde: undefined,
                  comDivCde: undefined,
                  takebackPaymentType: 'collect',
                  takebackSizeType: 'extraSmall',
                });
              }}
              list={[
                ...(deliveryEnterprises?.map((item) => ({
                  name: item.name,
                  id: item.id,
                })) ?? []),
                {
                  name: '그 외(자동회수 불가)',
                  id: null,
                },
              ]}
              subContent={
                isEditable &&
                `리스트 외 택배사는 자동 회수 접수가 불가합니다. ‘그 외’ 선택 시 자동 회수접수가 비활성 됩니다.`
              }
            />
            {formDeliveryEnterpriseId && (
              <>
                {showComDivCde ? (
                  <BaseForm
                    label="택배사 계약 정보"
                    required
                    content={
                      <Form.Item>
                        <div style={{ display: 'flex', gap: 10, flexDirection: 'column' }}>
                          <Form.Item required name="cusCde">
                            <div style={{ display: 'flex', flexDirection: 'column' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                                {form.getFieldDecorator('cusCde', {
                                  rules: [
                                    {
                                      validator: async () => {
                                        if (cusCdeSuccess) {
                                          return Promise.resolve();
                                        }

                                        return Promise.reject(new Error('신용코드와 협력사 코드를 다시 확인해주세요.'));
                                      },
                                    },
                                  ],
                                  initialValue: formCusCde ?? undefined,
                                  validateTrigger: ['onSubmit'],
                                  onChange: (value) => {
                                    setCusCdeSuccess(false);
                                    setComDivCdeSuccess(false);
                                    form.setFields({
                                      cusCde: {
                                        value,
                                        touched: false,
                                      },
                                    });
                                  },
                                })(<Input maxLength={10} placeholder="택배사 신용코드" disabled={!isEditable} />)}
                              </div>
                              {cusCdeSuccess && <div style={{ color: '#00E53D', fontSize: 12 }}>확인되었습니다.</div>}
                            </div>
                          </Form.Item>
                          <>
                            <Form.Item required name="comDivCde">
                              <div style={{ display: 'flex', flexDirection: 'column' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                                  {form.getFieldDecorator('comDivCde', {
                                    rules: [
                                      {
                                        validator: async () => {
                                          if (comDivCdeSuccess) {
                                            return Promise.resolve();
                                          }

                                          return Promise.reject(
                                            new Error('신용코드와 협력사 코드를 다시 확인해주세요.'),
                                          );
                                        },
                                      },
                                    ],
                                    initialValue: formComDivCde ?? undefined,
                                    validateTrigger: ['onSubmit'],
                                    onChange: (value) => {
                                      setComDivCdeSuccess(false);
                                      setCusCdeSuccess(false);
                                      form.setFields({
                                        comDivCde: {
                                          value,
                                          touched: false,
                                        },
                                      });
                                    },
                                  })(<Input maxLength={10} placeholder="택배사협력사 코드" disabled={!isEditable} />)}
                                </div>
                                {comDivCdeSuccess && (
                                  <div style={{ color: '#00E53D', fontSize: 12 }}>확인되었습니다.</div>
                                )}
                              </div>
                            </Form.Item>
                            {isEditable && (
                              <div style={{ color: '#6200ea', fontSize: 12, lineHeight: 'normal', marginTop: 10 }}>
                                택배사 계약 시 부여되는 신용코드, 협력사코드를 모두 입력한 뒤, 계약정보 확인을
                                눌러주세요.
                              </div>
                            )}
                          </>
                          {isEditable && (
                            <Button
                              style={{ lineHeight: 'normal', width: 'fit-content' }}
                              text="계약정보 확인"
                              isRadius
                              color="primary"
                              onClick={async (e) => {
                                e.preventDefault();
                                const formComDivCde = form.getFieldValue('comDivCde');
                                const formCusCde = form.getFieldValue('cusCde');

                                try {
                                  const isValid = await handleContractCodeValidations(deliveryEnterpriseId, {
                                    comDivCde: formComDivCde,
                                    cusCde: formCusCde,
                                  });

                                  setComDivCdeSuccess(isValid);
                                  setCusCdeSuccess(isValid);

                                  form.setFields({
                                    cusCde: {
                                      value: formCusCde,
                                      errors: isValid
                                        ? undefined
                                        : [new Error('신용코드와 협력사 코드를 다시 확인해주세요.')],
                                    },
                                    comDivCde: {
                                      value: formComDivCde,
                                      errors: isValid
                                        ? undefined
                                        : [new Error('신용코드와 협력사 코드를 다시 확인해주세요.')],
                                    },
                                  });
                                } catch (error) {
                                  setComDivCdeSuccess(false);

                                  form.setFields({
                                    cusCde: {
                                      value: formCusCde,
                                      errors: [new Error('신용코드와 협력사 코드를 다시 확인해주세요.')],
                                    },
                                    comDivCde: {
                                      value: formComDivCde,
                                      errors: [new Error('신용코드와 협력사 코드를 다시 확인해주세요.')],
                                    },
                                  });
                                }
                              }}
                            />
                          )}
                        </div>
                      </Form.Item>
                    }
                  />
                ) : (
                  <BaseForm
                    label="택배사 신용코드"
                    required
                    content={
                      <>
                        <Form.Item required name="cusCde">
                          <div style={{ display: 'flex', flexDirection: 'column' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                              {form.getFieldDecorator('cusCde', {
                                rules: [
                                  {
                                    validator: async () => {
                                      if (cusCdeSuccess) {
                                        return Promise.resolve();
                                      }

                                      return Promise.reject(
                                        new Error(
                                          "신용코드가 확인되지 않았습니다. 입력 후 '신용코드 확인'을 눌러주세요",
                                        ),
                                      );
                                    },
                                  },
                                ],
                                initialValue: formCusCde ?? undefined,
                                validateTrigger: ['onSubmit'],
                                onChange: () => {
                                  setCusCdeSuccess(false);
                                },
                              })(<Input maxLength={10} placeholder="택배사 신용코드" disabled={!isEditable} />)}
                              {isEditable && (
                                <Button
                                  style={{ lineHeight: 'normal' }}
                                  text="신용코드 확인"
                                  isRadius
                                  color="primary"
                                  onClick={async (e) => {
                                    e.preventDefault();
                                    const cusCde = form.getFieldValue('cusCde');
                                    try {
                                      const isValid = await handleContractCodeValidations(
                                        data?.takebackInfo?.deliveryEnterpriseId,
                                        { cusCde },
                                      );

                                      setCusCdeSuccess(isValid);

                                      form.setFields({
                                        cusCde: {
                                          value: cusCde,
                                          errors: isValid
                                            ? undefined
                                            : [new Error('신용코드가 유효하지 않습니다. 다시 입력해 주세요.')],
                                        },
                                      });
                                    } catch (error) {
                                      setCusCdeSuccess(false);

                                      form.setFields({
                                        cusCde: {
                                          value: cusCde,
                                          errors: [new Error('신용코드가 유효하지 않습니다. 다시 입력해 주세요.')],
                                        },
                                      });
                                    }
                                  }}
                                />
                              )}
                            </div>
                          </div>
                          {cusCdeSuccess && <div style={{ color: '#00E53D', fontSize: 12 }}>확인되었습니다.</div>}
                        </Form.Item>
                        {isEditable && (
                          <div style={{ color: '#6200ea', fontSize: 12 }}>
                            택배사 계약 시 부여되는 신용 코드를 입력해 주세요.
                          </div>
                        )}
                      </>
                    }
                  />
                )}
                <BaseForm
                  label="운임 구분"
                  required
                  content={
                    <RadioGroupForm
                      form={form}
                      name="takebackPaymentType"
                      isEditable={isEditable}
                      disabled={!isEditable}
                      initialValue={takebackPaymentType ?? 'collect'}
                      list={TAKEBACK_PAYMENT_TYPE_SELECT_LIST}
                    />
                  }
                  subContent={isEditable && `운임 구분을 선택해 주세요.`}
                />
                <BaseForm
                  label="택배 사이즈"
                  required
                  content={
                    <RadioGroupForm
                      form={form}
                      name="takebackSizeType"
                      isEditable={isEditable}
                      disabled={!isEditable}
                      initialValue={takebackSizeType ?? 'extraSmall'}
                      list={TAKEBACK_SIZE_TYPE_SELECT_LIST}
                    />
                  }
                  subContent={isEditable && `택배 사이즈를 선택해 주세요.`}
                />
              </>
            )}
          </>
        )}
      </div>
    </Panel>
  );
};
export default BrandDetailProductRegisterDelivery;
